import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../app/store/store';
import {
  fetchHelpdeskSettings,
  updateHelpdeskSettings,
  fetchTicketStatuses,
  createTicketStatus,
  updateTicketStatus,
  deleteTicketStatus,
} from '../../../entities/helpdesk-settings/model';
import { SettingsToggleWithInput } from '@shared/ui/settings-toggle';
import type { TicketStatusDef } from '../../../types/helpdeskSettingsInterfaces';

const PRESET_COLORS = [
  '#FB8C00', '#43A047', '#9E9E9E', '#1D4ED8', '#6D28D9', '#B42318', '#0E7490',
];

/* ── Модалка создания/редактирования статуса ── */
interface StatusModalProps {
  statusDef?: TicketStatusDef | null;
  onClose: () => void;
  onSave: (data: Partial<TicketStatusDef>) => void;
  onDelete?: (id: number) => void;
}

const StatusModal: React.FC<StatusModalProps> = ({
  statusDef,
  onClose,
  onSave,
  onDelete,
}) => {
  const isSystem = statusDef?.is_system ?? false;
  const [name, setName] = useState(statusDef?.name || '');
  const [code, setCode] = useState(statusDef?.code || '');
  const [color, setColor] = useState(statusDef?.color || PRESET_COLORS[3]);
  // Семантика: promiscuous-флаги двигают поведение (SLA, автозакрытие,
  // автоответ) — поэтому выбор через понятный select, а не чекбоксы.
  const initialKind = statusDef?.is_resolved
    ? 'resolved'
    : statusDef?.is_final
    ? 'final'
    : 'intermediate';
  const [kind, setKind] = useState<'intermediate' | 'resolved' | 'final'>(initialKind);
  const [isActive, setIsActive] = useState(statusDef?.is_active ?? true);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const codeValid = /^[a-z0-9_-]+$/.test(code);
  const canSubmit = name.trim().length > 0 && (isSystem || codeValid);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const data: Partial<TicketStatusDef> = {
      name: name.trim(),
      color,
      is_active: isActive,
    };
    if (!isSystem) {
      data.code = code.trim();
      data.is_resolved = kind === 'resolved';
      data.is_final = kind === 'final';
    }
    onSave(data);
  };

  const inputCls =
    'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-400';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-[#2d2c2c]">
            {statusDef ? 'Редактировать статус' : 'Добавить статус'}
            {isSystem && (
              <span className="ml-2 text-xs font-normal text-gray-400">системный</span>
            )}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Название <span className="text-red-500">*</span>
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Например: В работе"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Код <span className="text-red-500">*</span>
              </label>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.toLowerCase())}
                disabled={isSystem || Boolean(statusDef)}
                placeholder="in_progress"
                className={`${inputCls} disabled:bg-gray-50 disabled:text-gray-400`}
              />
              {!statusDef && code && !codeValid && (
                <p className="mt-1 text-xs text-red-500">
                  Только латиница, цифры, «-» и «_»
                </p>
              )}
            </div>
          </div>

          {!isSystem && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Поведение</label>
              <select
                value={kind}
                onChange={(e) => setKind(e.target.value as typeof kind)}
                className={inputCls}
              >
                <option value="intermediate">
                  Промежуточный — заявка в работе, SLA тикает
                </option>
                <option value="resolved">
                  Решённый — автоответ клиенту и таймер автозакрытия
                </option>
                <option value="final">Финальный — заявка закрыта</option>
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Цвет</label>
              <div className="flex items-center gap-2 flex-wrap">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className={`w-7 h-7 rounded-full border-2 transition-transform ${
                      color === c ? 'border-[#2d2c2c] scale-110' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: c }}
                    aria-label={c}
                  />
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Статус</label>
              <select
                value={isActive ? 'active' : 'inactive'}
                onChange={(e) => setIsActive(e.target.value === 'active')}
                disabled={isSystem}
                className={`${inputCls} disabled:bg-gray-50 disabled:text-gray-400`}
              >
                <option value="active">Активен</option>
                <option value="inactive">Неактивен</option>
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between pt-2">
            {statusDef && !isSystem && onDelete ? (
              <button
                type="button"
                onClick={() => {
                  if (confirmDelete) {
                    onDelete(statusDef.id);
                  } else {
                    setConfirmDelete(true);
                  }
                }}
                onBlur={() => setConfirmDelete(false)}
                className={`text-sm font-medium transition-colors ${
                  confirmDelete
                    ? 'text-white bg-[#e3282a] px-3 py-2 rounded-lg hover:bg-[#c82022]'
                    : 'text-gray-400 hover:text-[#e3282a]'
                }`}
              >
                {confirmDelete ? 'Точно удалить?' : 'Удалить'}
              </button>
            ) : (
              <span />
            )}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Отмена
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                className="px-4 py-2 text-sm font-medium text-white bg-[#e3282a] rounded-lg hover:bg-[#c82022] disabled:opacity-50"
              >
                {statusDef ? 'Сохранить' : 'Добавить'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const KIND_LABEL = (s: TicketStatusDef): string => {
  if (s.is_initial) return 'начальный';
  if (s.is_resolved) return 'решённый';
  if (s.is_final) return 'финальный';
  return 'промежуточный';
};

const StatusCloseSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { settings, status, ticketStatuses, ticketStatusesStatus, error } =
    useSelector((state: RootState) => state.helpdeskSettings);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState<TicketStatusDef | null>(null);

  useEffect(() => {
    if (status === 'idle') {
      dispatch(fetchHelpdeskSettings());
    }
    if (ticketStatusesStatus === 'idle') {
      dispatch(fetchTicketStatuses());
    }
  }, [dispatch, status, ticketStatusesStatus]);

  const handleUpdate = useCallback(
    (field: string, value: boolean | number) => {
      dispatch(updateHelpdeskSettings({ [field]: value }));
    },
    [dispatch],
  );

  const handleSaveStatus = useCallback(
    async (data: Partial<TicketStatusDef>) => {
      if (editingStatus) {
        await dispatch(updateTicketStatus({ id: editingStatus.id, data }));
      } else {
        await dispatch(createTicketStatus(data));
      }
      setModalOpen(false);
      setEditingStatus(null);
    },
    [dispatch, editingStatus],
  );

  const handleDeleteStatus = useCallback(
    async (id: number) => {
      await dispatch(deleteTicketStatus(id));
      setModalOpen(false);
      setEditingStatus(null);
    },
    [dispatch],
  );

  if (status === 'loading' || !settings) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-gray-200 rounded w-1/3"></div>
          <div className="h-12 bg-gray-100 rounded"></div>
          <div className="h-12 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Справочник статусов ── */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-semibold text-[#2d2c2c]">Статусы заявок</h2>
          <button
            onClick={() => { setEditingStatus(null); setModalOpen(true); }}
            className="px-4 py-2 text-sm font-medium text-white bg-[#e3282a] rounded-lg hover:bg-[#c82022] transition-colors"
          >
            + Добавить статус
          </button>
        </div>
        <p className="text-xs text-gray-400 mb-4">
          «Решённый» запускает автоответ и автозакрытие, «финальный» завершает работу,
          промежуточные держат SLA активным. Системные статусы удалить нельзя.
        </p>

        {error && <p className="mb-3 text-xs text-red-500">{error}</p>}

        {ticketStatuses.map((s) => (
          <div
            key={s.id}
            onClick={() => { setEditingStatus(s); setModalOpen(true); }}
            className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded transition-colors"
          >
            <div className="flex items-center gap-3 min-w-0">
              <span
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white flex-shrink-0"
                style={{ backgroundColor: s.color }}
              >
                {s.name}
              </span>
              <span className="text-xs text-gray-400 truncate">
                {s.code} · {KIND_LABEL(s)}
                {s.is_system && ' · системный'}
              </span>
            </div>
            {!s.is_active && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200 flex-shrink-0 ml-3">
                Неактивен
              </span>
            )}
          </div>
        ))}
      </div>

      {/* ── Автозакрытие ── */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-[#2d2c2c] mb-4">
          Автоматическое закрытие заявок
        </h2>

        <div className="divide-y divide-gray-100">
          <SettingsToggleWithInput
            label='Автоматически закрывать решённые заявки'
            description="Если клиент не ответил в течение указанного времени"
            checked={settings.auto_close_enabled}
            onToggle={(v) => handleUpdate('auto_close_enabled', v)}
            value={settings.auto_close_days}
            onValueChange={(v) => handleUpdate('auto_close_days', v)}
            prefix="Через"
            unit="дней"
          />

          <SettingsToggleWithInput
            label="Уведомить клиента перед автозакрытием"
            description="Email-уведомление за N дней до автоматического закрытия"
            checked={settings.warn_before_close_enabled}
            onToggle={(v) => handleUpdate('warn_before_close_enabled', v)}
            value={settings.warn_before_close_days}
            onValueChange={(v) => handleUpdate('warn_before_close_days', v)}
            prefix="За"
            unit="дня"
          />
        </div>
      </div>

      {modalOpen && (
        <StatusModal
          statusDef={editingStatus}
          onClose={() => { setModalOpen(false); setEditingStatus(null); }}
          onSave={handleSaveStatus}
          onDelete={handleDeleteStatus}
        />
      )}
    </div>
  );
};

export default StatusCloseSettings;
